import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { C, S } from './cedsStyles';

export default function CEDSAcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ready, setReady]           = useState(false);
  const [newPw, setNewPw]           = useState('');
  const [confirm, setConfirm]       = useState('');
  const [error, setError]           = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      // New flow: ?token_hash=...&type=invite — exchanged client-side so
      // email security scanners (which only GET the page, not run our JS)
      // can't burn the one-time token before the user clicks.
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type');
      if (tokenHash && type) {
        const { error: verifyErr } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
        if (cancelled) return;
        if (verifyErr) { setExpired(true); return; }
        setReady(true);
        return;
      }

      // Legacy flow: tokens already in the URL hash, picked up by detectSessionInUrl
      const { data: { session } } = await supabase.auth.getSession();
      if (session) { setReady(true); }
    }

    verify();

    // Also listen for async token processing (legacy flow)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') && session) {
        setReady(true);
      }
    });

    // If nothing happens in 8 seconds, the link is expired or invalid
    const timeout = setTimeout(() => { if (!cancelled) setExpired(true); }, 8000);

    return () => { cancelled = true; subscription.unsubscribe(); clearTimeout(timeout); };
  }, [searchParams]);

  async function handleSetPassword() {
    setError('');
    if (!newPw)            { setError('Password is required.'); return; }
    if (newPw.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    if (newPw !== confirm)  { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Your session has expired. Go back to the login page and use "Forgot password?" to get a new link.');
        setSubmitting(false);
        return;
      }

      const res = await fetch('/api/set-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ password: newPw }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setError(body.error ?? 'Failed to set password. Please try again.'); setSubmitting(false); return; }

      navigate('/ceds/portal/discussions');
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  }

  const inputStyle = { ...S.input, marginTop: 4 };

  return (
    <div style={{
      minHeight: '100vh', background: C.cream,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Nunito', 'Segoe UI', sans-serif", padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: C.white, borderRadius: 6,
        boxShadow: '0 4px 24px rgba(18,35,38,0.10)',
        overflow: 'hidden',
      }}>
        <div style={{ background: C.navy, padding: '28px 32px', textAlign: 'center' }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: C.gold,
            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
          }}>
            Panhandle Area Development District
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.white, lineHeight: 1.3 }}>
            CEDS Strategy Council Portal
          </div>
        </div>

        <div style={{ padding: '28px 32px' }}>
          {!ready ? (
            <div style={{ textAlign: 'center', fontSize: 14, padding: '8px 0' }}>
              {expired ? (
                <div>
                  <div style={{ color: C.red, fontWeight: 600, marginBottom: 8 }}>
                    This link has expired or already been used.
                  </div>
                  <div style={{ color: C.muted, marginBottom: 16 }}>
                    Go back to the login page and use <strong>Forgot password?</strong> to get a new link sent to your email.
                  </div>
                  <a href="/ceds/login" style={{
                    ...S.btn.primary, display: 'inline-block', textDecoration: 'none',
                    padding: '9px 20px', fontSize: 14,
                  }}>
                    Back to Login
                  </a>
                </div>
              ) : (
                <div style={{ color: C.muted }}>Verifying your invite link…</div>
              )}
            </div>
          ) : (
            <>
              <h3 style={{ color: C.navy, margin: '0 0 6px', fontSize: '1.1rem' }}>
                Welcome — Set Your Password
              </h3>
              <p style={{ color: C.muted, fontSize: 13, margin: '0 0 20px' }}>
                Create a password to finish setting up your council portal account.
              </p>

              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>New Password</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  placeholder="At least 8 characters"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={S.label}>Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  style={inputStyle}
                  onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
                />
              </div>

              {error && (
                <div style={{
                  background: '#fdf0ed', border: `1px solid ${C.red}`,
                  borderRadius: 4, padding: '9px 14px',
                  fontSize: 13, color: C.red, marginBottom: 16,
                }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleSetPassword}
                disabled={submitting}
                style={{ ...S.btn.primary, width: '100%', padding: '11px', fontSize: 15, opacity: submitting ? 0.65 : 1 }}
              >
                {submitting ? 'Saving…' : 'Set Password & Enter Portal'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
